import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Upload, ExternalLink } from 'lucide-react';

const FIELDS = {
  articles: [['title', 'Title'], ['excerpt', 'Summary', 'area'], ['content', 'Article content (HTML: use <h2>, <p>, <ul>, <a href="/courses/...">)', 'html'], ['category', 'Category'], ['author', 'Author'], ['read_minutes', 'Read time (minutes)', 'number'], ['meta_title', 'SEO title (under 60 characters)'], ['meta_description', 'SEO description (under 160 characters)', 'area'], ['cover_url', 'Cover image', 'file']],
  downloads: [['title', 'Title'], ['description', 'Description', 'area'], ['category', 'Category'], ['file_type', 'File type'], ['file_url', 'File (PDF, Word, Excel)', 'file'], ['cover_url', 'Cover image', 'file']],
  products: [['title', 'Title'], ['author', 'Author'], ['description', 'Description', 'area'], ['format', 'Format'], ['external_url', 'Store link (Amazon etc.) — leave blank to sell the e-book here'], ['price', 'Price £ (only when selling here)', 'number'], ['file_url', 'E-book file (only when selling here)', 'file'], ['cover_url', 'Cover image', 'file']],
};
const DEFAULTS = { articles: { status: 'draft', category: 'Guides' }, downloads: { status: 'draft', category: 'Checklists', file_type: 'PDF' }, products: { status: 'draft', format: 'PDF e-book', price: 0 } };
const TABS = [['articles', 'Articles'], ['downloads', 'Free downloads'], ['products', 'Books & e-books'], ['leads', 'Download leads']];

const Editor = ({ kind, item, onClose, onSaved }) => {
  const { api } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item?.id) api.get(`/hub/admin/${kind}/${item.id}`).then((r) => setForm(r.data));
    else setForm({ ...DEFAULTS[kind] });
  }, [api, kind, item]);
  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const upload = async (k, file) => {
    const fd = new FormData(); fd.append('file', file); fd.append('alt_text', form.title || 'Knowledge Hub');
    try { const r = await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); set(k, r.data.url); toast.success('Uploaded'); }
    catch { toast.error('Upload failed'); }
  };
  const save = async (status) => {
    setBusy(true);
    const payload = { ...form, status };
    ['read_minutes', 'price'].forEach((k) => { if (payload[k] === '' || payload[k] === undefined) delete payload[k]; else if (k in payload) payload[k] = Number(payload[k]); });
    Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
    try {
      if (item?.id) await api.put(`/hub/admin/${kind}/${item.id}`, payload); else await api.post(`/hub/admin/${kind}`, payload);
      toast.success(status === 'published' ? 'Published' : 'Saved as draft'); onSaved();
    } catch (e) {
      const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : Array.isArray(d) ? d.map((x) => x.msg).join(' ') : 'Could not save');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hub-editor-dialog">
        <DialogHeader><DialogTitle>{item?.id ? 'Edit' : 'New'} {kind === 'products' ? 'book' : kind.slice(0, -1)}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {FIELDS[kind].map(([k, label, type]) => (
            <label key={k} className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">{label}</span>
              {type === 'area' ? <Textarea rows={3} value={form[k] || ''} onChange={(e) => set(k, e.target.value)} data-testid={`hub-field-${k}`} />
                : type === 'html' ? <Textarea rows={14} className="font-mono text-xs" value={form[k] || ''} onChange={(e) => set(k, e.target.value)} data-testid={`hub-field-${k}`} />
                  : type === 'file' ? (
                    <div className="flex gap-2 items-center">
                      <Input value={form[k] || ''} onChange={(e) => set(k, e.target.value)} placeholder="Upload or paste a link" data-testid={`hub-field-${k}`} />
                      <label className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-sm cursor-pointer hover:bg-slate-50"><Upload className="h-4 w-4" />Upload
                        <input type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(k, e.target.files[0])} data-testid={`hub-upload-${k}`} /></label>
                    </div>
                  ) : <Input type={type === 'number' ? 'number' : 'text'} value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)} data-testid={`hub-field-${k}`} />}
              {k === 'meta_title' && <span className="text-xs text-slate-400">{(form[k] || '').length}/60</span>}
              {k === 'meta_description' && <span className="text-xs text-slate-400">{(form[k] || '').length}/160</span>}
            </label>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => save('draft')} disabled={busy} data-testid="hub-save-draft">Save draft</Button>
          <Button onClick={() => save('published')} disabled={busy} className="bg-green-600 hover:bg-green-700" data-testid="hub-publish">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Publish</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminHubPage() {
  const { api, user } = useAuth();
  const [tab, setTab] = useState('articles');
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const isAdmin = user?.role === 'super_admin';
  const load = useCallback(() => {
    setItems(null);
    api.get(tab === 'leads' ? '/hub/admin/leads' : `/hub/admin/${tab}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [api, tab]);
  useEffect(() => { load(); }, [load]);
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try { const r = await api.delete(`/hub/admin/${tab}/${it.id}`); toast.success(r.data.message); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };
  const canEdit = tab !== 'leads' && (tab !== 'products' || isAdmin);
  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="space-y-6" data-testid="admin-hub-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Knowledge Hub</h1>
            <p className="text-sm text-slate-500 mt-1">Articles, free downloads and recommended books shown at <a href="/knowledge-hub" target="_blank" rel="noreferrer" className="text-[#20acf6]">/knowledge-hub <ExternalLink className="inline h-3 w-3" /></a></p>
          </div>
          {canEdit && <Button onClick={() => setEditing({})} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="hub-new-btn"><Plus className="h-4 w-4 mr-1" /> New</Button>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {TABS.map(([id, label]) => <Button key={id} size="sm" variant={tab === id ? 'default' : 'outline'} className={tab === id ? 'bg-[#2a3e5d]' : ''} onClick={() => setTab(id)} data-testid={`admin-hub-tab-${id}`}>{label}</Button>)}
        </div>
        <Card className="border-slate-200"><CardContent className="p-0">
          {!items ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#20acf6]" /></div>
            : !items.length ? <p className="p-8 text-sm text-slate-500 text-center">Nothing here yet.</p>
              : tab === 'leads' ? (
                <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-600"><tr><th className="px-4 py-2.5 text-left">Name</th><th className="px-4 py-2.5 text-left">Email</th><th className="px-4 py-2.5 text-left">Organisation</th><th className="px-4 py-2.5 text-left">Download</th><th className="px-4 py-2.5 text-left">Marketing</th><th className="px-4 py-2.5 text-left">Date</th></tr></thead>
                  <tbody>{items.map((l) => <tr key={l.id} className="border-t border-slate-100"><td className="px-4 py-2.5">{l.name}</td><td className="px-4 py-2.5">{l.email}</td><td className="px-4 py-2.5">{l.organisation || '—'}</td><td className="px-4 py-2.5">{l.download_title}</td><td className="px-4 py-2.5">{l.consent_marketing ? 'Yes' : 'No'}</td><td className="px-4 py-2.5 text-xs text-slate-500">{l.created_at?.slice(0, 10)}</td></tr>)}</tbody></table>
              ) : (
                <div className="divide-y divide-slate-100">
                  {items.map((it) => (
                    <div key={it.id} className="flex items-center gap-4 px-5 py-3.5" data-testid={`admin-hub-item-${it.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{it.title}</p>
                        <p className="text-xs text-slate-500">{it.category || it.format}{it.download_count !== undefined && ` · ${it.download_count} downloads`}{it.sales !== undefined && ` · ${it.sales} sales`}{it.external_url && ' · store link'}</p>
                      </div>
                      <Badge className={it.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{it.status === 'published' ? 'Published' : 'Draft'}</Badge>
                      {canEdit && <Button size="icon" variant="ghost" onClick={() => setEditing(it)} data-testid={`admin-hub-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>}
                      {isAdmin && <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(it)} data-testid={`admin-hub-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  ))}
                </div>
              )}
        </CardContent></Card>
      </div>
      {editing && <Editor kind={tab} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </DashboardLayout>
  );
}
